import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Radio,
  RadioGroup,
  Badge,
  Spinner,
  FormControl,
  FormLabel,
  Box,
  useToast
} from '@chakra-ui/react';
import { FaSave, FaExclamationTriangle } from 'react-icons/fa';
import { verifyCurrentUser } from '../../services/userSettingsService';
// Helper function for formatting dates
const formatDateRelative = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 5) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const SaveSlotModal = ({ isOpen, onClose, onSave, slotData, isSaving, accentColor }) => {
  const [selectedSlot, setSelectedSlot] = useState(1);
  const [slotName, setSlotName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const toast = useToast();
  
  useEffect(() => {
    if (slotData?.save_slots && slotData.save_slots[selectedSlot - 1]) {
      setSlotName(slotData.save_slots[selectedSlot - 1].name || `Save ${selectedSlot}`);
    } else {
      setSlotName(`Save ${selectedSlot}`);
    }
  }, [selectedSlot, slotData]);
  
  // Add this effect to verify user when modal opens
  useEffect(() => {
    if (isOpen) {
      verifyCurrentUser().then(result => {
        if (!result.valid) {
          toast({
            title: "Session Error",
            description: "Your login session has expired. Please refresh the page.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          onClose();
        } else if (result.changed) {
          toast({
            title: "User Changed",
            description: "Detected a different user session. Your data will be reloaded.",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
        }
      });
    }
  }, [isOpen, toast, onClose]);
  
  const handleSave = async () => {
    if (!slotName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for this save slot.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setVerifying(true);
    
    // Verify current user before saving
    const userStatus = await verifyCurrentUser();
    setVerifying(false);
    
    if (!userStatus.valid) {
      toast({
        title: "Authentication Error",
        description: "Please refresh the page and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    onSave(selectedSlot, slotName);
  };
 
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={accentColor}>Save Widget Configuration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="start">
            <Text>Select a save slot and provide a descriptive name:</Text>
            
            <FormControl isRequired>
              <FormLabel>Slot Name</FormLabel>
              <Input
                value={slotName}
                onChange={(e) => setSlotName(e.target.value)}
                placeholder="Enter a name for this save"
                maxLength={30}
              />
            </FormControl>
            
            <RadioGroup onChange={(val) => setSelectedSlot(parseInt(val))} value={selectedSlot.toString()}>
              <VStack spacing={3} align="start" width="100%">
                {slotData?.save_slots ? (
                  slotData.save_slots.map((slot, index) => (
                    <Box
                      key={index}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      width="100%"
                      borderColor={selectedSlot === slot.slot ? accentColor : "gray.200"}
                      bg={selectedSlot === slot.slot ? `${accentColor}10` : "transparent"}
                    >
                      <HStack justifyContent="space-between">
                        <Radio value={slot.slot.toString()} colorScheme="green">
                          <Text fontWeight="medium">Slot {slot.slot}: {slot.name || `Save ${slot.slot}`}</Text>
                        </Radio>
                        <HStack>
                          {slot.active && (
                            <Badge colorScheme="green">Active</Badge>
                          )}
                          {slot.last_saved && (
                            <Text fontSize="xs" color="gray.500">
                              {formatDateRelative(slot.last_saved)}
                            </Text>
                          )}
                        </HStack>
                      </HStack>
                      
                      {slot.settings && selectedSlot === slot.slot && (
                        <Text fontSize="xs" color="red.500" mt={2}>
                          <FaExclamationTriangle style={{ display: 'inline', marginRight: '4px' }} />
                          This will overwrite the existing save in this slot
                        </Text>
                      )}
                    </Box>
                  ))
                ) : (
                  <Spinner size="sm" />
                )}
              </VStack>
            </RadioGroup>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="green" 
            leftIcon={isSaving || verifying ? <Spinner size="sm" /> : <FaSave />}
            onClick={handleSave}
            isDisabled={isSaving || verifying}
          >
            {isSaving ? "Saving..." : verifying ? "Verifying..." : "Save"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SaveSlotModal;